import React, { useEffect, useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { classNames, calculateIkrProgress, krHasIkrOwner } from '../../lib/utils';
import ProgressDonut from '../../components/ui/ProgressDonut';
import KRSection from './KRSection';

function getStatusInfo(progress) {
  if (progress >= 1.0) return {
    label: 'Ahead',
    headerBg: 'bg-emerald-50/60',
    badgeBg: 'bg-emerald-100',
    badgeText: 'text-emerald-700',
  };
  if (progress >= 0.7) return {
    label: 'On Track',
    headerBg: 'bg-green-50/40',
    badgeBg: 'bg-green-100',
    badgeText: 'text-green-700',
  };
  if (progress >= 0.3) return {
    label: 'At Risk',
    headerBg: 'bg-amber-50/40',
    badgeBg: 'bg-amber-100',
    badgeText: 'text-amber-700',
  };
  return {
    label: 'Behind',
    headerBg: 'bg-red-50/30',
    badgeBg: 'bg-red-100',
    badgeText: 'text-red-700',
  };
}

export default function ObjectiveCardFY2627({ objective, onUpdate, onDelete, users, viewer, onOwnerAssigned, nextKrCode }) {
  const [isExpanded, setIsExpanded] = useState(false);
  /** A freshly created objective opens straight into rename mode. */
  const [isEditing, setIsEditing] = useState(objective.objective === 'New Objective');
  const [objectiveText, setObjectiveText] = useState(objective.objective);
  const [isEditingCode, setIsEditingCode] = useState(false);
  const [codeText, setCodeText] = useState(objective.code || '');
  /** Why "Add KR" refused — the objective has no code to derive one from. */
  const [addKrError, setAddKrError] = useState('');
  const canEdit = viewer.role === 'Admin';

  // Keep local drafts in step with Firestore updates from elsewhere.
  useEffect(() => { setObjectiveText(objective.objective); }, [objective.objective]);
  useEffect(() => { setCodeText(objective.code || ''); setAddKrError(''); }, [objective.code]);

  const visibleKRs = useMemo(() => {
    const all = objective.keyResults || [];
    if (canEdit) return all;
    // Trimmed with blanks dropped on both sides, so a stray '' grant cannot
    // match an uncoded KR and reveal every one of them.
    const krs = new Set((viewer.accessibleKRs || [])
      .map(code => String(code || '').trim()).filter(Boolean));
    // The objective-level check in App.js lets a viewer in on either a grant or
    // ownership, so this filter has to honour both — otherwise someone reaches
    // the card through ownership and finds their own KR filtered out of it.
    const viewerId = viewer.uid || viewer.id;
    return all.filter(kr => (
      krs.has(String(kr.code || '').trim()) || krHasIkrOwner(kr, viewerId)
    ));
  }, [objective.keyResults, canEdit, viewer.accessibleKRs, viewer.uid, viewer.id]);

  const handleUpdateObjectiveField = async (field, value) => {
    await onUpdate(objective.id, { [field]: value });
  };

  const handleUpdateKR = (updatedKr) => {
    const updatedKeyResults = (objective.keyResults || []).map(kr => kr.id === updatedKr.id ? updatedKr : kr);
    handleUpdateObjectiveField('keyResults', updatedKeyResults);
  };

  /**
   * A key result with no code is invisible to every non-Admin, and no grant can
   * ever name it — so the code is assigned up front rather than left for someone
   * to fill in later. When the objective itself has no usable code there is
   * nothing to derive from, and the KR is refused instead of created blank.
   */
  const addKeyResult = () => {
    const code = nextKrCode ? nextKrCode(objective) : null;
    if (!code) {
      setAddKrError('Give this objective a code (like O13) first — a key result with no code is invisible to everyone except Admins.');
      return;
    }
    setAddKrError('');
    const newKr = { id: `kr_${Date.now()}`, code, text: 'New Key Result', teamKRs: [] };
    handleUpdateObjectiveField('keyResults', [...(objective.keyResults || []), newKr]);
  };

  const handleDeleteKR = (krId) => {
    const next = (objective.keyResults || []).filter(kr => kr.id !== krId);
    handleUpdateObjectiveField('keyResults', next);
  };

  const overallProgress = useMemo(() => {
    const allIkrs = visibleKRs.flatMap(kr => (kr.teamKRs || []).flatMap(tkr => tkr.individualKRs || []));
    if (allIkrs.length === 0) return 0;
    return allIkrs.reduce((sum, ikr) => sum + calculateIkrProgress(ikr), 0) / allIkrs.length;
  }, [visibleKRs]);

  // Count KRs by status
  const krStatusCounts = useMemo(() => {
    const counts = { onTrack: 0, atRisk: 0, behind: 0, ahead: 0 };
    visibleKRs.forEach(kr => {
      const allIkrs = (kr.teamKRs || []).flatMap(tkr => tkr.individualKRs || []);
      if (allIkrs.length === 0) return;
      const prog = allIkrs.reduce((sum, ikr) => sum + calculateIkrProgress(ikr), 0) / allIkrs.length;
      if (prog >= 1.0) counts.ahead++;
      else if (prog >= 0.7) counts.onTrack++;
      else if (prog >= 0.3) counts.atRisk++;
      else counts.behind++;
    });
    return counts;
  }, [visibleKRs]);

  const status = getStatusInfo(overallProgress);

  return (
    <div className="bg-white rounded-xl shadow-md mb-5 border border-gray-200/80 transition-shadow hover:shadow-lg overflow-hidden">

      {/* ── Objective Header ──────────────────────────────────── */}
      <div className={`p-4 ${status.headerBg}`}>
        <div className="flex items-start justify-between gap-4">

          {/* Left: chevron + code + title + KR summary */}
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-1.5 flex-shrink-0 p-1 rounded-full hover:bg-white/70 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </button>

            <div className="flex-1 min-w-0">
              <div className="flex items-center flex-wrap gap-2 mb-1.5">
                {isEditingCode ? (
                  <input
                    type="text"
                    value={codeText}
                    onChange={(e) => setCodeText(e.target.value)}
                    onBlur={() => { setIsEditingCode(false); handleUpdateObjectiveField('code', codeText.trim()); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { setIsEditingCode(false); handleUpdateObjectiveField('code', codeText.trim()); }
                      if (e.key === 'Escape') { setCodeText(objective.code || ''); setIsEditingCode(false); }
                    }}
                    className="w-20 text-xs font-mono font-bold text-indigo-700 bg-white border border-indigo-300 rounded-md px-2 py-1 focus:ring-1 focus:ring-blue-500 outline-none"
                    placeholder="O13"
                    autoFocus
                  />
                ) : (
                  <span
                    className={classNames(
                      'text-xs font-mono font-bold text-indigo-700 bg-indigo-100 px-2.5 py-1 rounded-md',
                      canEdit && 'cursor-pointer hover:bg-indigo-200'
                    )}
                    onClick={() => canEdit && setIsEditingCode(true)}
                    title={canEdit ? 'Click to edit code' : undefined}
                  >
                    {objective.code || '—'}
                  </span>
                )}
                <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${status.badgeBg} ${status.badgeText}`}>
                  {status.label}
                </span>
              </div>

              {isEditing ? (
                <input
                  type="text"
                  value={objectiveText}
                  onChange={(e) => setObjectiveText(e.target.value)}
                  onBlur={() => { setIsEditing(false); handleUpdateObjectiveField('objective', objectiveText); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { setIsEditing(false); handleUpdateObjectiveField('objective', objectiveText); }
                    if (e.key === 'Escape') { setObjectiveText(objective.objective); setIsEditing(false); }
                  }}
                  className="w-full p-1.5 border border-gray-300 rounded-lg text-lg font-bold focus:ring-1 focus:ring-blue-500 bg-white"
                  placeholder="Objective name"
                  autoFocus
                />
              ) : (
                <h3
                  className={classNames('text-lg font-bold text-gray-800 leading-snug', canEdit && 'hover:text-blue-600 cursor-pointer')}
                  onClick={() => canEdit && setIsEditing(true)}
                  title={canEdit ? 'Click to rename' : undefined}
                >
                  {objective.objective}
                </h3>
              )}

              {/* KR status pills */}
              {visibleKRs.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  <span className="text-xs text-gray-400">{visibleKRs.length} KR{visibleKRs.length !== 1 ? 's' : ''}</span>
                  {krStatusCounts.ahead > 0 && (
                    <span className="text-xs font-medium bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">⭐ {krStatusCounts.ahead} Ahead</span>
                  )}
                  {krStatusCounts.onTrack > 0 && (
                    <span className="text-xs font-medium bg-green-100 text-green-700 px-1.5 py-0.5 rounded">✓ {krStatusCounts.onTrack} On Track</span>
                  )}
                  {krStatusCounts.atRisk > 0 && (
                    <span className="text-xs font-medium bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">⚠ {krStatusCounts.atRisk} At Risk</span>
                  )}
                  {krStatusCounts.behind > 0 && (
                    <span className="text-xs font-medium bg-red-100 text-red-700 px-1.5 py-0.5 rounded">● {krStatusCounts.behind} Behind</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right: progress donut + delete */}
          <div className="flex items-center gap-2 flex-shrink-0 mt-1">
            <ProgressDonut progress={overallProgress} size={56} />
            {canEdit && (
              <button
                onClick={() => onDelete(objective.id)}
                className="text-gray-300 hover:text-red-600 p-1.5 rounded-full hover:bg-red-100 transition-colors"
                title="Delete objective"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Expanded: KR Sections ────────────────────────────── */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="mt-3 space-y-1">
            {visibleKRs.map(kr => (
              <KRSection
                key={kr.id}
                kr={kr}
                onUpdate={handleUpdateKR}
                onDelete={handleDeleteKR}
                users={users}
                viewer={viewer}
                onOwnerAssigned={onOwnerAssigned}
              />
            ))}
          </div>
          {canEdit && (
            <div className="mt-3 pl-6">
              <button
                onClick={addKeyResult}
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-semibold"
              >
                <Plus size={15} /> Add KR
              </button>
              {addKrError && <p className="text-xs text-red-600 mt-1.5">{addKrError}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
